import type { WeaponDefinition, WeaponContext, WeaponResult } from './types';
import { Projectile } from '../entities/projectile';
import { WA_PHYSICS } from '../core/constants';
import { computeSafeSpawnPosition } from './spawnHelper';

/**
 * Aduka (GunBound mobile, Shot 2): fires three tracer shots in sequence
 * (WA_PHYSICS.ADUKA.SHOT_INTERVAL_MS apart) from the SAME spawn with the SAME
 * launch velocity. Each tracer explodes, deals damage and CRATERS terrain on
 * impact, and calls down its own Thor strike (which also craters). All three
 * share an impact lock: the first tracer records where it lands and the rest
 * explode at that exact spot - so the volley lands together AND drills the
 * ground. (Without the lock the later shots fly through the first crater and
 * sail off the map.)
 */
type ImpactLock = { x: number | null; y: number | null };

function makeTracer(
  originX: number,
  originY: number,
  vx: number,
  vy: number,
  ownerId: string,
  lock: ImpactLock,
): Projectile {
  return new Projectile({
    x: originX,
    y: originY,
    vx,
    vy,
    type: 'aduka',
    timer: -1, // impact-only, no fuse
    windAffected: false, // keep every shot on an identical trajectory
    explosionRadius: WA_PHYSICS.ADUKA.EXPLOSION_RADIUS,
    maxDamage: WA_PHYSICS.ADUKA.MAX_DAMAGE,
    ownerId,
    impactLock: lock,
    // The tracer itself does no damage/crater - the Thor strike it calls down
    // (per impact) is the whole payload.
    noContactExplosion: true,
  });
}

export const WeaponAduka: WeaponDefinition = {
  id: 'aduka',
  name: 'Aduka',
  icon: 'aduka.png',
  category: 'thrown',
  maxAmmo: 2,
  maxUsesPerTurn: 1,
  powerCharging: true,
  timerAdjustable: false,
  showAimGuide: true,
  allowRetreat: true,

  fire(ctx: WeaponContext): WeaponResult {
    const { worm, angle, power, world } = ctx;

    const speed = (power / 100) * 750;
    const rad = (angle * Math.PI) / 180;
    const aimRad = worm.facing === 'right' ? rad : Math.PI - rad;
    const spawn = computeSafeSpawnPosition(worm, aimRad, 20, world);
    const vx = Math.cos(aimRad) * speed;
    const vy = -Math.sin(aimRad) * speed;

    // First shot launches now; the rest follow on a fixed timer, all on the
    // identical trajectory. They share one lock so they land on the same spot.
    const lock: ImpactLock = { x: null, y: null };
    const delayedEntities = [];
    for (let i = 1; i < WA_PHYSICS.ADUKA.COUNT; i++) {
      delayedEntities.push({
        delayMs: i * WA_PHYSICS.ADUKA.SHOT_INTERVAL_MS,
        entity: makeTracer(spawn.x, spawn.y, vx, vy, worm.id, lock),
      });
    }

    return {
      success: true,
      entities: [makeTracer(spawn.x, spawn.y, vx, vy, worm.id, lock)],
      delayedEntities,
    };
  },
};
